import {
  Device,
  DeviceGroupDto,
  MultiValueVariableDto,
  SingleValueVariableDto,
  VariableDto,
} from '@thingslog/repositories';
import { useMemo } from 'react';
import {
  deviceGroupsQueryClient,
  devicesQueryClient,
} from '../../../../../../clients/ReactQueryClients/ReactQueryClients';
import useDetermineCompanyId from '../../../../../../hooks/useDetermineCompanyId';
import { QuerySelectOption, QuerySettingsOptionSources } from './models';
import { useTranslation } from 'react-i18next';

const createVariableOption = (
  variable: SingleValueVariableDto | MultiValueVariableDto,
  variableLabel: string
): QuerySelectOption => ({
  value: `\${${variable.name}}`,
  label: `${variable.label} (${variable.name})`,
  searchText: `${variableLabel} ${variable.name} ${variable.label}`.toLowerCase(),
  isVariable: true,
});

export const useQuerySettingsOptions = (variables: VariableDto[]): QuerySettingsOptionSources => {
  const { t } = useTranslation();
  const companyId = useDetermineCompanyId();
  const { useDevicesData } = devicesQueryClient;
  const { useDeviceGroupsData } = deviceGroupsQueryClient;

  const devicesQuery = useDevicesData(companyId, 0, 9999, null, null);
  const deviceGroupsQuery = useDeviceGroupsData(companyId);

  const singleDeviceOptions = useMemo((): QuerySelectOption[] => {
    const variableOptions = variables
      .filter(
        (variable: VariableDto): variable is SingleValueVariableDto =>
          variable.type === 'single-value'
      )
      .map((variable: SingleValueVariableDto) =>
        createVariableOption(variable, t('dashboard_variable', 'variable'))
      );

    const deviceOptions =
      devicesQuery.data?.content.map(
        (device: Device): QuerySelectOption => ({
          value: device.number,
          label: `${device.name || device.number} (${device.number})`,
          searchText: `${device.name || ''} ${device.number}`.toLowerCase(),
        })
      ) ?? [];

    return [...variableOptions, ...deviceOptions];
  }, [devicesQuery.data, t, variables]);

  const multiDeviceOptions = useMemo((): QuerySelectOption[] => {
    const variableOptions = variables
      .filter(
        (variable: VariableDto): variable is MultiValueVariableDto =>
          variable.type === 'multi-value'
      )
      .map((variable: MultiValueVariableDto) =>
        createVariableOption(variable, t('dashboard_variable', 'variable'))
      );

    const deviceOptions =
      devicesQuery.data?.content.map(
        (device: Device): QuerySelectOption => ({
          value: device.number,
          label: `${device.name || device.number} (${device.number})`,
          searchText: `${device.name || ''} ${device.number}`.toLowerCase(),
        })
      ) ?? [];

    return [...variableOptions, ...deviceOptions];
  }, [devicesQuery.data, t, variables]);

  const sensorIndexOptions = useMemo((): QuerySelectOption[] => {
    const variableOptions = variables
      .filter(
        (variable: VariableDto): variable is SingleValueVariableDto =>
          variable.type === 'single-value'
      )
      .map((variable: SingleValueVariableDto) =>
        createVariableOption(variable, t('dashboard_variable', 'variable'))
      );

    const sensorOptions = Array.from(
      { length: 256 },
      (_: unknown, index: number): QuerySelectOption => ({
        value: index.toString(),
        label: t('dashboard_sensor_index_option', 'Sensor {{index}}', { index }),
        searchText: `${t('sensor', 'Sensor')} ${index}`.toLowerCase(),
      })
    );

    return [...variableOptions, ...sensorOptions];
  }, [t, variables]);

  const deviceGroupOptions = useMemo((): QuerySelectOption[] => {
    const variableOptions = variables
      .filter(
        (variable: VariableDto): variable is MultiValueVariableDto =>
          variable.type === 'multi-value'
      )
      .map((variable: MultiValueVariableDto) =>
        createVariableOption(variable, t('dashboard_variable', 'variable'))
      );

    const groupOptions =
      deviceGroupsQuery.data?.map(
        (group: DeviceGroupDto): QuerySelectOption => ({
          value: group.deviceGroupId.toString(),
          label: group.deviceGroupName,
          searchText: group.deviceGroupName.toLowerCase(),
        })
      ) ?? [];

    return [...variableOptions, ...groupOptions];
  }, [deviceGroupsQuery.data, t, variables]);

  return {
    singleDeviceOptions,
    multiDeviceOptions,
    sensorIndexOptions,
    deviceGroupOptions,
    deviceOptionsLoading: devicesQuery.isLoading,
    deviceGroupOptionsLoading: deviceGroupsQuery.isLoading,
  };
};
